import React, { useEffect, useRef, useState } from 'react'
import { createChart, ColorType, IChartApi, ISeriesApi } from 'lightweight-charts'

interface AdvancedTradingChartProps {
  symbol: string
  timeframe: string
  width?: number
  height?: number
}

interface OHLCData {
  time: number
  open: number
  high: number
  low: number
  close: number
}

interface ChartData {
  time: number
  open: number
  high: number
  low: number
  close: number
  vwap?: number
  sma_50?: number
  sma_200?: number
  rsi_14?: number
  macd?: number
  macd_signal?: number
  volume?: number
}

export default function AdvancedTradingChart({
  symbol,
  timeframe,
  width = 800,
  height = 400
}: AdvancedTradingChartProps) {
  const chartContainerRef = useRef<HTMLDivElement>(null)
  const [chart, setChart] = useState<IChartApi | null>(null)
  const [candlestickSeries, setCandlestickSeries] = useState<ISeriesApi<'Candlestick'> | null>(null)
  const [data, setData] = useState<ChartData[]>([])
  const [currentOHLC, setCurrentOHLC] = useState<OHLCData | null>(null)
  const [isDarkTheme, setIsDarkTheme] = useState(false)
  const [loading, setLoading] = useState(true)

  // Indicator states
  const [showVWAP, setShowVWAP] = useState(false)
  const [showSMA50, setShowSMA50] = useState(false)
  const [showSMA200, setShowSMA200] = useState(false)
  const [showRSI, setShowRSI] = useState(false)
  const [showMACD, setShowMACD] = useState(false)

  // Indicator series refs
  const [vwapSeries, setVwapSeries] = useState<ISeriesApi<'Line'> | null>(null)
  const [sma50Series, setSma50Series] = useState<ISeriesApi<'Line'> | null>(null)
  const [sma200Series, setSma200Series] = useState<ISeriesApi<'Line'> | null>(null)

  const getThemeColors = () => {
    return isDarkTheme ? {
      background: '#1e222d',
      textColor: '#ffffff',
      gridColor: '#2a2a2a',
      borderColor: '#3a3a3a',
      upColor: '#26a69a',
      downColor: '#ef5350'
    } : {
      background: '#ffffff',
      textColor: '#000000',
      gridColor: '#f0f0f0',
      borderColor: '#cccccc',
      upColor: '#26a69a',
      downColor: '#ef5350'
    }
  }

  // Load chart data
  const loadChartData = async () => {
    try {
      setLoading(true)
      console.log(`Loading data for ${symbol}, ${timeframe}`)

      const response = await fetch(`/admin/api_bars.php?ticker=${symbol}&timeframe=${timeframe}&limit=500`)

      if (response.ok) {
        const apiData = await response.json()
        console.log('API Response:', apiData)

        if (Array.isArray(apiData) && apiData.length > 0) {
          setData(apiData)

          // Set current OHLC
          const latest = apiData[apiData.length - 1]
          setCurrentOHLC({
            time: latest.time,
            open: latest.open,
            high: latest.high,
            low: latest.low,
            close: latest.close,
          })

          return apiData
        }
      }

      // Fallback to mock data
      console.log('Using mock data')
      const mockData = generateMockData()
      setData(mockData)
      setCurrentOHLC({
        time: mockData[mockData.length - 1].time,
        open: mockData[mockData.length - 1].open,
        high: mockData[mockData.length - 1].high,
        low: mockData[mockData.length - 1].low,
        close: mockData[mockData.length - 1].close,
      })

      return mockData
    } catch (error) {
      console.error('Error loading data:', error)
      const mockData = generateMockData()
      setData(mockData)
      setCurrentOHLC({
        time: mockData[mockData.length - 1].time,
        open: mockData[mockData.length - 1].open,
        high: mockData[mockData.length - 1].high,
        low: mockData[mockData.length - 1].low,
        close: mockData[mockData.length - 1].close,
      })
      return mockData
    } finally {
      setLoading(false)
    }
  }

  const generateMockData = (): ChartData[] => {
    const mockData: ChartData[] = []
    const basePrice = 1.0900
    let currentPrice = basePrice

    for (let i = 100; i >= 0; i--) {
      const time = Math.floor(Date.now() / 1000) - i * 3600
      const open = currentPrice
      const change = (Math.random() - 0.5) * 0.004
      const close = open + change
      const high = Math.max(open, close) + Math.random() * 0.002
      const low = Math.min(open, close) - Math.random() * 0.002

      mockData.push({
        time,
        open,
        high,
        low,
        close,
        volume: Math.random() * 1000000,
      })

      currentPrice = close
    }

    return mockData
  }

  // Initialize chart
  useEffect(() => {
    if (!chartContainerRef.current) return

    const colors = getThemeColors()

    const newChart = createChart(chartContainerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: colors.background },
        textColor: colors.textColor,
      },
      width: width,
      height: height,
      grid: {
        vertLines: { color: colors.gridColor },
        horzLines: { color: colors.gridColor },
      },
      crosshair: {
        mode: 1,
      },
      rightPriceScale: {
        borderColor: colors.borderColor,
        borderVisible: false,
      },
      timeScale: {
        borderColor: colors.borderColor,
        timeVisible: true,
        secondsVisible: false,
        borderVisible: false,
      },
    })

    const newCandlestickSeries = newChart.addCandlestickSeries({
      upColor: colors.upColor,
      downColor: colors.downColor,
      borderVisible: false,
      wickUpColor: colors.upColor,
      wickDownColor: colors.downColor,
    })

    setChart(newChart)
    setCandlestickSeries(newCandlestickSeries)

    // Handle resize
    const handleResize = () => {
      if (chartContainerRef.current) {
        newChart.applyOptions({
          width: chartContainerRef.current.clientWidth,
        })
      }
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      newChart.remove()
    }
  }, [width, height, isDarkTheme])

  // Load data when chart is ready or symbol/timeframe changes
  useEffect(() => {
    if (chart && candlestickSeries) {
      loadChartData().then((chartData) => {
        if (chartData) {
          const candleData = chartData.map(d => ({
            time: d.time,
            open: d.open,
            high: d.high,
            low: d.low,
            close: d.close,
          }))
          candlestickSeries.setData(candleData)
        }
      })
    }
  }, [chart, candlestickSeries, symbol, timeframe])

  // VWAP Toggle
  const toggleVWAP = () => {
    if (!chart) return

    if (showVWAP && vwapSeries) {
      chart.removeSeries(vwapSeries)
      setVwapSeries(null)
      setShowVWAP(false)
    } else {
      const newSeries = chart.addLineSeries({
        color: '#0ea5e9',
        lineWidth: 2,
      })

      const vwapData = data.filter(d => d.vwap != null).map(d => ({
        time: d.time as any,
        value: d.vwap!
      }))

      if (vwapData.length > 0) {
        newSeries.setData(vwapData)
      } else {
        // Calculate VWAP if not available
        let cumPV = 0, cumVol = 0
        const calculatedVwap = data.map(d => {
          const tp = (d.high + d.low + d.close) / 3
          const vol = d.volume || 1000
          cumPV += tp * vol
          cumVol += vol
          return {
            time: d.time as any,
            value: cumPV / cumVol
          }
        })
        newSeries.setData(calculatedVwap)
      }

      setVwapSeries(newSeries)
      setShowVWAP(true)
    }
  }

  // SMA50 Toggle
  const toggleSMA50 = () => {
    if (!chart) return

    if (showSMA50 && sma50Series) {
      chart.removeSeries(sma50Series)
      setSma50Series(null)
      setShowSMA50(false)
    } else {
      const newSeries = chart.addLineSeries({
        color: '#8b5cf6',
        lineWidth: 1,
      })

      const smaData = data.filter(d => d.sma_50 != null).map(d => ({
        time: d.time as any,
        value: d.sma_50!
      }))

      if (smaData.length > 0) {
        newSeries.setData(smaData)
      } else {
        // Calculate SMA50 if not available
        const calculatedSma: any[] = []
        for (let i = 49; i < data.length; i++) {
          const sum = data.slice(i-49, i+1).reduce((s, d) => s + d.close, 0)
          calculatedSma.push({ time: data[i].time as any, value: sum / 50 })
        }
        newSeries.setData(calculatedSma)
      }

      setSma50Series(newSeries)
      setShowSMA50(true)
    }
  }

  // SMA200 Toggle
  const toggleSMA200 = () => {
    if (!chart) return

    if (showSMA200 && sma200Series) {
      chart.removeSeries(sma200Series)
      setSma200Series(null)
      setShowSMA200(false)
    } else {
      const newSeries = chart.addLineSeries({
        color: '#f59e0b',
        lineWidth: 1,
      })

      const smaData = data.filter(d => d.sma_200 != null).map(d => ({
        time: d.time as any,
        value: d.sma_200!
      }))

      if (smaData.length > 0) {
        newSeries.setData(smaData)
      } else {
        // Calculate SMA200 if not available
        const calculatedSma: any[] = []
        for (let i = 199; i < data.length; i++) {
          const sum = data.slice(i-199, i+1).reduce((s, d) => s + d.close, 0)
          calculatedSma.push({ time: data[i].time as any, value: sum / 200 })
        }
        if (calculatedSma.length > 0) {
          newSeries.setData(calculatedSma)
        }
      }

      setSma200Series(newSeries)
      setShowSMA200(true)
    }
  }

  const formatPrice = (price: number) => {
    return price.toFixed(5)
  }

  const getCurrentTime = () => {
    return new Date().toUTCString().split(' ')[4] + ' UTC'
  }

  if (loading) {
    return (
      <div className="w-full">
        <div className="mb-4 p-4 bg-card border border-border rounded-lg">
          <div className="flex items-center justify-center h-16">
            <div className="text-muted-foreground">Loading chart data...</div>
          </div>
        </div>
        <div className="border border-border rounded-lg bg-background overflow-hidden">
          <div className="flex items-center justify-center" style={{ height: `${height}px` }}>
            <div className="text-muted-foreground">Loading...</div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full">
      {/* Chart Header */}
      <div className="mb-4 p-4 bg-card border border-border rounded-lg">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          {/* Left side - Symbol and OHLC */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold text-foreground">{symbol}</h3>
              <span className="px-2 py-1 bg-muted text-muted-foreground rounded text-sm">
                {timeframe}
              </span>
            </div>

            {/* OHLC Display */}
            {currentOHLC && (
              <div className="flex gap-4 text-sm">
                <span className="text-muted-foreground">O: <span className="text-foreground font-mono">{formatPrice(currentOHLC.open)}</span></span>
                <span className="text-muted-foreground">H: <span className="text-green-600 font-mono">{formatPrice(currentOHLC.high)}</span></span>
                <span className="text-muted-foreground">L: <span className="text-red-600 font-mono">{formatPrice(currentOHLC.low)}</span></span>
                <span className="text-muted-foreground">C: <span className="text-foreground font-mono">{formatPrice(currentOHLC.close)}</span></span>
              </div>
            )}
          </div>

          {/* Right side - Controls */}
          <div className="flex items-center gap-2">
            <div className="text-sm text-muted-foreground font-mono">
              {getCurrentTime()}
            </div>
            <button
              onClick={() => setIsDarkTheme(!isDarkTheme)}
              className="btn btn-secondary btn-sm"
              title="Toggle Theme"
            >
              {isDarkTheme ? '☀️' : '🌙'}
            </button>
          </div>
        </div>
      </div>

      {/* Chart Container */}
      <div className="border border-border rounded-lg bg-background overflow-hidden">
        <div
          ref={chartContainerRef}
          style={{ height: `${height}px` }}
        />
      </div>

      {/* Chart Controls */}
      <div className="mt-4 p-4 bg-card border border-border rounded-lg">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={toggleVWAP}
            className={`btn btn-sm ${showVWAP ? 'btn-primary' : 'btn-secondary'}`}
            title="Volume Weighted Average Price"
          >
            VWAP
          </button>
          <button
            onClick={toggleSMA50}
            className={`btn btn-sm ${showSMA50 ? 'btn-primary' : 'btn-secondary'}`}
            title="Simple Moving Average 50"
          >
            SMA50
          </button>
          <button
            onClick={toggleSMA200}
            className={`btn btn-sm ${showSMA200 ? 'btn-primary' : 'btn-secondary'}`}
            title="Simple Moving Average 200"
          >
            SMA200
          </button>
          <button
            onClick={() => setShowRSI(!showRSI)}
            className={`btn btn-sm ${showRSI ? 'btn-primary' : 'btn-secondary'}`}
            title="Relative Strength Index (Coming Soon)"
            disabled
          >
            RSI
          </button>
          <button
            onClick={() => setShowMACD(!showMACD)}
            className={`btn btn-sm ${showMACD ? 'btn-primary' : 'btn-secondary'}`}
            title="Moving Average Convergence Divergence (Coming Soon)"
            disabled
          >
            MACD
          </button>
        </div>

        <div className="mt-2 text-sm text-muted-foreground">
          Click indicators to toggle. Data from TradingView Lightweight Charts
        </div>
      </div>
    </div>
  )
}